import { TrendingUp, TrendingDown, Flame, Info } from 'lucide-react';

/**
 * DividendChangeBadge
 * --------------------
 * Pill that surfaces a recent (last 90 days) ≥10% change in a stock's
 * dividend payment vs the prior payment. Backend pre-computes the
 * change at refresh time and writes:
 *   stock.recent_div_change_type: 'raise' | 'cut' | null
 *   stock.recent_div_change_pct:  number (signed % change)
 *   stock.recent_div_last_amount, recent_div_prev_amount
 *   stock.recent_div_last_date:   'YYYY-MM-DD'
 * so this component is a pure render — no fetch.
 *
 * Streak-unbroken bonus: when the latest event is a `raise` AND the
 * stock is a King / Aristocrat / Achiever, the badge gets an extra
 * flame icon + the word "STREAK!" appended. Pure visual celebration
 * for long-streak stocks that just bumped their dividend again.
 */
export default function DividendChangeBadge({ stock, size = 'md' }) {
  const t = stock?.recent_div_change_type;
  const pct = stock?.recent_div_change_pct;
  if (!t || pct == null) return null;

  const isRaise = t === 'raise';
  const Icon = isRaise ? TrendingUp : TrendingDown;
  const sign = pct > 0 ? '+' : '';
  const pctStr = `${sign}${pct.toFixed(1)}%`;
  const tier = (stock?.dividend_tier || '').toLowerCase();
  const isStreak = isRaise && (tier === 'king' || tier === 'aristocrat' || tier === 'achiever');

  const tooltip = (() => {
    const last = stock.recent_div_last_amount;
    const prev = stock.recent_div_prev_amount;
    const date = stock.recent_div_last_date;
    const change = isRaise ? 'raised' : 'cut';
    const detail =
      last != null && prev != null
        ? ` — last payment $${Number(last).toFixed(4)} vs prior $${Number(prev).toFixed(4)}`
        : '';
    const streakNote = isStreak
      ? ` · Streak unbroken — this ${tier.charAt(0).toUpperCase() + tier.slice(1)} just kept its raise streak alive!`
      : '';
    return `Dividend ${change} ${pctStr}${detail}${date ? ` (paid ${date})` : ''}${streakNote}`;
  })();

  const small = size === 'sm';
  const cls = isStreak
    ? 'bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200 border-amber-400/70 dark:border-amber-600/70 ring-1 ring-amber-400/40'
    : isRaise
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-300/60 dark:border-emerald-700/60'
      : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-300/60 dark:border-rose-700/60';

  const hasPatternNote = Boolean(stock?.profile_data?.dividend_pattern_note);

  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide border cursor-help ${cls}`}
      title={tooltip}
      data-testid={`div-change-badge-${stock.ticker}`}
    >
      <Icon className={small ? 'w-2 h-2' : 'w-2.5 h-2.5'} />
      {isRaise ? 'Raised' : 'Cut'} {pctStr}
      {hasPatternNote && (
        <Info
          className={`${small ? 'w-2 h-2' : 'w-2.5 h-2.5'} ml-0.5 opacity-80`}
          data-testid={`div-change-pattern-info-${stock.ticker}`}
        />
      )}
      {isStreak && (
        <>
          <Flame className={`${small ? 'w-2 h-2' : 'w-2.5 h-2.5'} text-orange-500 ml-0.5`} data-testid={`div-streak-flame-${stock.ticker}`} />
          <span className="ml-0.5">Streak!</span>
        </>
      )}
    </span>
  );
}
